import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { RECIPES } from '@constants/subscription';
import { KTSVG, toAbsoluteUrl } from '@helpers/index';
import { getDisabledRecipes } from '@modules/subscriptions/utils/getDisabledRecipes';

import {
  getAllergiesSelector,
  getHealthIssuesSelector,
  getPetSelector,
  getSubscriptionRecipesSelector,
} from '../../../core/selectors';
import { IPet, ReferenceOption } from '../../../core/types';
import { usePetProfileUpdateContext } from '../context';
import { ArrowRightIcon, HeartIcon, LoadingSpinner, WarningIcon } from '../icons';
import { RECIPE_INFO } from '../types';

export const PetHealthStep: React.FC = () => {
  const intl = useIntl();
  const {
    healthInfo,
    setHealthInfo,
    goNext,
    isRecalculating,
    isUpdating,
    hasChanges,
    handleSubmit,
    handleRecalculate,
    selectedVariant,
  } = usePetProfileUpdateContext();

  // When no recipe conflict, we recalculate then auto-submit once variant is ready
  const [pendingAutoSubmit, setPendingAutoSubmit] = React.useState(false);

  const allergies = useSelector(getAllergiesSelector);
  const healthIssues = useSelector(getHealthIssuesSelector);
  const pet = useSelector(getPetSelector);
  const currentRecipes = useSelector(getSubscriptionRecipesSelector);

  const { allergies: selectedAllergies, healthIssues: selectedHealthIssues } = healthInfo;

  const tempPet = {
    ...pet,
    allergies: selectedAllergies,
    health_issues: selectedHealthIssues,
  } as IPet;

  const disabledRecipes = getDisabledRecipes(tempPet as any);
  const conflictingRecipes = currentRecipes.filter((r: RECIPES) => disabledRecipes.includes(r));
  const isCurrentRecipeDisabled = conflictingRecipes.length > 0;

  // Check for opportunities (previously disabled recipes becoming enabled)
  const oldDisabledRecipes = getDisabledRecipes(pet as any);
  const hasNewOption = oldDisabledRecipes.some((r: RECIPES) => !disabledRecipes.includes(r));

  const warningMessage = React.useMemo(() => {
    if (!isCurrentRecipeDisabled) return null;

    // Brand names, so they are joined rather than translated. The separator is
    // padded here instead of inside the message, so the catalogue never carries
    // leading/trailing whitespace.
    const proteins = conflictingRecipes
      .map((r: RECIPES) => RECIPE_INFO[r]?.protein || r)
      .join(conflictingRecipes.length > 1 ? ` ${intl.formatMessage({ id: 'GLOBAL.AND' })} ` : '');

    return intl.formatMessage({ id: 'PET_PROFILE.HEALTH.WARNING' }, { proteins });
  }, [conflictingRecipes, isCurrentRecipeDisabled, intl]);

  const successMessage = React.useMemo(() => {
    if (!hasNewOption) return null;
    return intl.formatMessage(
      { id: 'PET_PROFILE.HEALTH.SUCCESS' },
      { name: pet?.name ?? intl.formatMessage({ id: 'GLOBAL.DOG_FALLBACK' }) },
    );
  }, [hasNewOption, pet?.name, intl]);

  const toggleSelection = (item: string, list: string[], field: 'allergies' | 'healthIssues') => {
    const newList = list.includes(item) ? list.filter((i) => i !== item) : [...list, item];
    setHealthInfo({ ...healthInfo, [field]: newList });
  };

  const handleNext = () => {
    if (!isCurrentRecipeDisabled && !hasNewOption) {
      // No recipe conflict — recalculate first, then auto-submit once variant is ready
      setPendingAutoSubmit(true);
      handleRecalculate();
      return;
    }
    goNext();
  };

  // Auto-submit once recalculation completes and variant is available
  React.useEffect(() => {
    if (pendingAutoSubmit && selectedVariant && !isRecalculating) {
      setPendingAutoSubmit(false);
      handleSubmit();
    }
  }, [pendingAutoSubmit, selectedVariant, isRecalculating, handleSubmit]);

  return (
    <div className="pet-profile-update-modal p-8 max-w-[60rem] mx-auto bg-white rounded-[3rem]">
      <div className="pet-info-step__header">
        <h3 className="text-2xl font-bold">
          {intl.formatMessage({ id: 'PET_PROFILE.HEALTH.EDIT_TITLE' })}
        </h3>
      </div>

      <div className="mb-[3rem]">
        <h4 className="pet-info-step__label">
          <WarningIcon />
          {intl.formatMessage({ id: 'PET.ALLERGIES' })}
        </h4>
        <ul className="ppu-health-list">
          {allergies?.map(({ name, label }: ReferenceOption) => {
            // `name` is the identity: the key, the checked state, the submitted
            // value and what getDisabledRecipes matches. Only `label` is shown.
            const isSelected = selectedAllergies.includes(name);
            return (
              <li key={name}>
                <label
                  className={`ppu-health-item ${isSelected ? 'ppu-health-item--selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelection(name, selectedAllergies, 'allergies')}
                    className="form-checkbox h-5 w-5 text-primary rounded"
                  />
                  <span>{label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="mb-8">
        <h4 className="pet-info-step__label">
          <HeartIcon />
          {intl.formatMessage({ id: 'PET.HEALTH_ISSUES' })}
        </h4>
        <ul className="ppu-health-list">
          {healthIssues?.map(({ name, label }: ReferenceOption) => {
            const isSelected = selectedHealthIssues.includes(name);
            return (
              <li key={name}>
                <label
                  className={`ppu-health-item ${isSelected ? 'ppu-health-item--selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelection(name, selectedHealthIssues, 'healthIssues')}
                    className="form-checkbox h-5 w-5 text-primary rounded"
                  />
                  <span>{label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="flex justify-end pt-4 flex-col">
        {isCurrentRecipeDisabled && (
          <div className="ppu-alert">
            <div className="ppu-alert-health">
              <KTSVG
                path={toAbsoluteUrl('/media/svg/general/info.svg')}
                className="svg-icon svg-icon-1"
              />
            </div>
            <div className="ppu-alert__content">
              <strong className="font-bold">
                {intl.formatMessage({ id: 'GLOBAL.ATTENTION' })}
              </strong>
              <span>{warningMessage}</span>
            </div>
          </div>
        )}
        {successMessage && !isCurrentRecipeDisabled && (
          <div className="ppu-alert">
            <div className="ppu-alert-health">
              <KTSVG
                path={toAbsoluteUrl('/media/svg/general/info.svg')}
                className="svg-icon svg-icon-1"
              />
            </div>
            <div className="ppu-alert__content">
              <strong className="font-bold">
                {intl.formatMessage({ id: 'PET_PROFILE.HEALTH.GREAT_NEWS' })}
              </strong>
              <span>{successMessage}</span>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={handleNext}
            disabled={isRecalculating || isUpdating || !hasChanges}
            className={`ppu-action-button ppu-action-button--primary ${
              isRecalculating || isUpdating ? 'ppu-action-button--loading' : ''
            } ${!hasChanges ? 'ppu-action-button--disabled' : ''}`}>
            {isRecalculating || isUpdating ? (
              <>
                <LoadingSpinner />
                {intl.formatMessage({
                  id: isUpdating ? 'GLOBAL.SAVING' : 'GLOBAL.CALCULATING',
                })}
              </>
            ) : (
              <>
                {intl.formatMessage({ id: 'GLOBAL.NEXT' })}
                <ArrowRightIcon />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
